import { useEffect, useMemo, useState } from "react";
import { useAlert } from "../../../shared/components/AlertProvider";
import Card from "../../../shared/components/Card";
import CommonSelect from "../../../shared/components/CommonSelect";
import Flex from "../../../shared/components/Flex";
import GridInfo from "../../../shared/components/GridInfo";
import Input from "../../../shared/components/Input";
import ManageButton from "../../../shared/components/ManageButton";
import ManagementPage from "../../../shared/components/ManagementPage";
import Padding from "../../../shared/components/Padding";
import Spacer from "../../../shared/components/Spacer";
import Text from "../../../shared/components/Text";
import useDevice from "../../../shared/hooks/useDevice";
import { addTooltipField } from "../../../shared/utils/gridTitleUtil";
import AgGridTable from "../../common/AgGridTable";
import { responseMsg } from "../../common/ResponseMsg";
import ScrollModal from "../../common/ScrollModal";
import CodeAddModal from "./CodeAddModal";
import CodeEditModal from "./CodeEditModal";

function Code({name}) {
    const [render, setRender] = useState(false);
    const [searchObj, setSearchObj] = useState({req: {name: ""}});
    const [codes, setCodes] = useState();
    const [selectedCode1, setSelectedCode1] = useState();
    const [selectedCode2, setSelectedCode2] = useState();
    const [selectedCode3, setSelectedCode3] = useState();
    const [updateModal, setUpdateModal] = useState({});
    const [addModal, setAddModal] = useState({});
    const device = useDevice();
    const isMobile = device !== 'desktop';
    const [useYn, setUseYn] = useState("Y");
    const showAlert = useAlert();
    const [loading, setLoading] = useState(false);

    const useYnOption = [
        {text: "사용", value: "Y"},
        {text: "미사용", value: "N"}
    ]

    const rendering = () => {
        closeUpdateModal();
        closeAddModal();
        setRender(!render);
    }

    const leftGridConfig = {
        columnDefs: addTooltipField(
            [
                { headerName: '코드', field: 'groupCode', width: 150},
                { headerName: '코드명', field: 'name', width: 150, cellStyle: { textAlign: "left" } },
                { headerName: '비고', field: 'comment', width: 150, cellStyle: { textAlign: "left" } },
                { headerName: '사용여부', field: 'useYn', width: 120, valueFormatter: (params) => params.value === 'Y' ? '사용' : '미사용'},
            ]
        ),
        pagination: false,
        onCellClicked: (cell) => {
            setSelectedCode1(cell.data);
            setSelectedCode2(codes.find(ele => ele.parent === cell.data.id));
        },
        rowSelection: 'single',
        rowData: codes?.filter(ele => ele.depth == 1),
    };

    const level1RowData = useMemo(
        () => codes?.filter(ele => ele.depth == 1) ?? [],
        [codes]
    );

    const level2RowData = useMemo(
        () => codes?.filter(ele => selectedCode1?.id === ele.parent) ?? [],
        [codes, selectedCode1]
    );

    const level3RowData = useMemo(
        () => codes?.filter(ele => selectedCode2?.id === ele.parent) ?? [],
        [codes, selectedCode2]
    );

    const centerGridConfig = {
        columnDefs: addTooltipField(
            [
                { headerName: '코드', field: 'id', width: 150},
                { headerName: '코드명', field: 'name', width: 150, cellStyle: { textAlign: "left" } },
                { headerName: '비고', field: 'comment', width: 150, cellStyle: { textAlign: "left" } },
                { headerName: '사용여부', field: 'useYn', width: 120, valueFormatter: (params) => params.value === 'Y' ? '사용' : '미사용'},
            ]
        ),
        pagination: false,
        onCellClicked: (cell) => {
            setSelectedCode2(cell.data);
        },
        onCellDoubleClicked: (cell) => openUpdateModal(cell),
        rowSelection: 'single',
        rowData: level2RowData,
    };

    const rightGridConfig = {
        columnDefs: addTooltipField(
            [
                { headerName: '코드', field: 'id', width: 150},
                { headerName: '코드명', field: 'name', width: 150, cellStyle: { textAlign: "left" } },
                { headerName: '비고', field: 'comment', width: 150, cellStyle: { textAlign: "left" } },
                { headerName: '사용여부', field: 'useYn', width: 120, valueFormatter: (params) => params.value === 'Y' ? '사용' : '미사용'},
            ]
        ),
        pagination: false,
        onCellClicked: (cell) => {
            setSelectedCode3(cell.data);
        },
        onCellDoubleClicked: (cell) => openUpdateModal(cell),
        rowSelection: 'single',
        rowData: level3RowData,
    };

    // 모달 (등록)
    const openAddModal = (type) => {

        setAddModal({
            ...addModal,
            headerText: `${type} 코드 등록`,
            open: true,
            close: closeAddModal,
            main: <CodeAddModal
                type={type}
                selectedCode1={selectedCode1}
                selectedCode2={selectedCode2}
                rendering={rendering}
            />
        });
    };

    const closeAddModal = () => {

        setAddModal({
            ...addModal,
            open : false,
        });
    };

    // 모달 (수정)
    const openUpdateModal = (cell) => {

        setUpdateModal({
            ...updateModal,
            open: true,
            close: closeUpdateModal,
            main: <CodeEditModal
                code={cell.data}
                rendering={rendering}
            />
        });
    };

    const closeUpdateModal = () => {

        setUpdateModal({
            ...updateModal,
            open : false,
        });
    };

    const apiGetCodeList = (page) => {
        setLoading(true);
        let url = '/api/system/codes?'
        const pagingSize = 20;

        if (page) {
            url += `page=${page-1}&`
            url += `size=${pagingSize}&`
        }

        url += `useYn=${useYn}&`

        if(searchObj) {
            Object.keys(searchObj.req).forEach(ele => {
                if (searchObj.req[ele]) {
                    url += `${ele}=${searchObj.req[ele]}&`
                }
            })
            
            fetch(url ,{
                method:"GET"
            })
            .then((res) => res.json())
            .then((json) => {
                if (json.code === responseMsg.SUCCESS.code) {
                    setCodes(json.data)
    
                    if (!selectedCode1) {
                        const code1 = json.data.find(ele => ele.depth == 1);
                        setSelectedCode1(code1);
                        setSelectedCode2(json.data.find(ele => ele.parent == code1.id));
    
                    } else {
                        setSelectedCode1(json.data.find(ele => selectedCode1.id === ele.id));
                        setSelectedCode2(json.data.find(ele => selectedCode2.id === ele.id));
                    }
    
                }  else if (json.code === responseMsg.FAIL.code) {
                    showAlert({title: "경고", text: "통신이 지연되고 있습니다.", type: "warning"});
                }
            })
            .catch((error) => {
            })
            .finally(() => {setLoading(false)});
        } 
    }

    const search = () => {
        const codeNm = document.getElementById("codeNm").value ?? "";
        setSearchObj({req : {name : codeNm}})
    }


    useEffect(() => {
        apiGetCodeList();
    },[render,searchObj, useYn])

    return (
        <ManagementPage
            title="코드관리"
            actions={
                <>
                    <ManageButton
                        onClick={search} 
                        btnType='search'
                    />
                </>
            }
            searchArea={
                <>
                    <Flex flexDirection={isMobile ? 'column' : 'row'} gap={isMobile ? 8 : 30}>
                        <Flex alignItems='center'>
                            <Text size={"sm"} style={{width: "70px", flexShrink: 0}}>코드명</Text>
                            <Input 
                                id='codeNm' 
                                fullWidth={isMobile} 
                                name="codeNm" 
                                placeholder='코드명을 입력해 주세요' 
                                style={{flex: 1}} 
                                maxLength={200} 
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        search();
                                    } 
                                }}
                                autoFocus
                            />
                        </Flex>

                        <div style={{ width: isMobile ? "auto" : "324px" }}>
                            <CommonSelect label='사용여부' fullWidth value={useYn} data={useYnOption} onChange={setUseYn}/>
                        </div>
                    </Flex>
                </>
            }
        >
            <Flex flexDirection={isMobile ? "column" : "row"} gap={15} style={{flex: 1}}>
                {/* Level 1 */}
                <Card style={{ flex: isMobile ? "none" : 3, height: isMobile ? "358px" : "auto", display: 'flex'}}>
                    <Padding paddingX={10} paddingY={15} style={{ flex: 1, display: "flex", flexDirection: "column" }}>
                        <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", height: "34px"}}>
                            <GridInfo title="Level 1" width={450}
                                content="항목을 클릭하면 Level2 목록에 선택한 코드의 하위 코드가 표시됩니다.
                                        항목을 더블클릭하면 선택한 코드를 수정하는 화면이 열립니다." />
                            
                        </div>
                        <Spacer direction='vertical' size={15} />

                        <div style={{ flex: 1}}>
                            <AgGridTable
                                rowData={level1RowData}
                                columnDefs={leftGridConfig.columnDefs}
                                rowDragManaged={true}
                                onCellClicked={leftGridConfig.onCellClicked}
                                onRowDoubleClicked={openUpdateModal}
                                selectedRow={selectedCode1}
                                tooltipShowDelay={0}
                                tooltipHideDelay={2000}
                                loading={loading}
                            />
                        </div>
                    </Padding>
                </Card>
                {/* Level 2 */}
                <Card style={{ flex: isMobile ? "none" : 3, height: isMobile ? "358px" : "auto", display: 'flex'}}>
                    <Padding paddingX={10} paddingY={15} style={{ flex: 1, display: "flex", flexDirection: "column" }}>
                        <div style={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                            <GridInfo title="Level 2" width={450}
                                    content="항목을 클릭하면 Level3 목록에 선택한 코드의 하위 코드가 표시됩니다.
                                        항목을 더블클릭하면 선택한 코드를 수정하는 화면이 열립니다." />
                            <ManageButton btnType="regist" onClick={() => {openAddModal("Level 2")}} />
                        </div>
                        <Spacer direction='vertical' size={15} />

                        <div style={{ flex: 1}}>
                            <AgGridTable 
                                rowData={centerGridConfig.rowData}
                                columnDefs={centerGridConfig.columnDefs}
                                rowDragManaged={true}
                                onCellClicked={centerGridConfig.onCellClicked}
                                onCellDoubleClicked={centerGridConfig.onCellDoubleClicked}
                                selectedRow={selectedCode2}
                                tooltipShowDelay={0}
                                tooltipHideDelay={2000}
                                loading={loading}
                            />
                        </div>
                    </Padding>
                </Card>
                {/* Level 3 */}
                <Card style={{ flex: isMobile ? "none" : 3, height: isMobile ? "358px" : "auto", display: 'flex'}}>
                    <Padding paddingX={10} paddingY={15} style={{ flex: 1, display: "flex", flexDirection: "column" }}>
                        <div style={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                            <GridInfo title="Level 3" width={400}
                                    content="항목을 더블클릭하면 선택한 코드를 수정하는 화면이 열립니다." />
                            <ManageButton btnType="regist" onClick={() => {openAddModal("Level 3")}}/>
                        </div>
                        <Spacer direction='vertical' size={15} />

                        <div style={{ flex: 1}}>
                            <AgGridTable
                                rowData={rightGridConfig.rowData}
                                columnDefs={rightGridConfig.columnDefs}
                                rowDragManaged={true}
                                onRowClicked={rightGridConfig.onCellClicked}
                                onRowDoubleClicked={openUpdateModal}
                                selectedRow={selectedCode3}
                                tooltipShowDelay={0}
                                tooltipHideDelay={2000}
                                loading={loading}
                            />
                        </div>
                    </Padding>
                </Card>
            </Flex>
            <ScrollModal
                headerText="코드 등록"
                size={"595px"}
                modal={addModal}
            >
            </ScrollModal>

            <ScrollModal
                headerText="코드 수정"
                size={"595px"}
                modal={updateModal}
            >
            </ScrollModal>
        </ManagementPage>
    );
}

export default Code;
